/**
 * Recognising OUR OWN Firebase Storage objects from a URL.
 *
 * /api/image-proxy is unauthenticated by necessity: it exists so the crop dialog
 * can read an image onto a canvas without tainting it, and it runs before any
 * user context is established. Its only real control is therefore which URLs it
 * agrees to fetch.
 *
 * Host-only allowlisting is not enough for that. `storage.googleapis.com` and
 * `firebasestorage.googleapis.com` front EVERY bucket on Google Cloud, not just
 * ours, so allowing the hosts alone let anyone pump any public object on GCS
 * through our origin and bill the egress to us. The bucket is the part that has
 * to match.
 *
 * Pure and dependency-free so it can be unit tested and imported anywhere.
 */

/**
 * The app's Storage bucket, plus the legacy `appspot.com` alias that Firebase
 * projects created before the `firebasestorage.app` rename still answer on.
 * Both address the same objects, and download URLs minted at different times
 * can carry either, so both are ours.
 */
export const OWN_STORAGE_BUCKETS: readonly string[] = [
  'studio-282433670-dd879.firebasestorage.app',
  'studio-282433670-dd879.appspot.com',
];

/**
 * One percent-decoded path segment, or `null` when it cannot be decoded.
 *
 * `decodeURIComponent` THROWS on a malformed escape (`%E0%A4%A`, a lone `%`), and
 * a URL carrying one parses perfectly happily — `new URL()` does not decode, and
 * neither does zod's `.url()`. So the throw reached /api/image-proxy's caller
 * uncaught and turned what should have been a 403 into a 500 on an endpoint that
 * is unauthenticated by necessity and can be looped by anyone.
 *
 * Returning null is the right answer rather than merely the safe one: a segment
 * that is not valid percent-encoding is not a bucket name, so "not one of ours"
 * is exactly what it means.
 */
function decodeSegment(raw: string): string | null {
  try {
    return decodeURIComponent(raw).toLowerCase();
  } catch {
    return null;
  }
}

/**
 * The bucket a Google Storage URL addresses, or `null` when the URL is not a
 * recognised Storage URL at all.
 *
 * Three shapes are in circulation and all three appear in real data:
 *
 *   firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>   (getDownloadURL)
 *   storage.googleapis.com/<bucket>/<path>                          (path style)
 *   <bucket>.storage.googleapis.com/<path>                          (virtual host)
 *
 * Returns the bucket verbatim; the caller decides whether it is one of ours.
 * Anything else — including a host that merely ends with the right suffix on a
 * different registrable domain — yields null.
 *
 * @param parsed A parsed URL. Callers should have already required https.
 */
export function storageBucketFromUrl(parsed: URL): string | null {
  const host = parsed.hostname.toLowerCase();

  // Virtual-hosted style: <bucket>.storage.googleapis.com
  if (host.endsWith('.storage.googleapis.com')) {
    const bucket = host.slice(0, -'.storage.googleapis.com'.length);
    return bucket || null;
  }

  const segments = parsed.pathname.split('/').filter(Boolean);

  // Firebase download URLs: /v0/b/<bucket>/o/<encodedPath>
  if (host === 'firebasestorage.googleapis.com') {
    if (segments[0] === 'v0' && segments[1] === 'b' && segments[2]) {
      return decodeSegment(segments[2]);
    }
    return null;
  }

  // Path style: /<bucket>/<object...>
  if (host === 'storage.googleapis.com') {
    return segments[0] ? decodeSegment(segments[0]) : null;
  }

  return null;
}

/**
 * True when `parsed` addresses an object in one of this app's own buckets.
 *
 * @param parsed A parsed URL. Callers should have already required https.
 */
export function isOwnStorageUrl(parsed: URL): boolean {
  const bucket = storageBucketFromUrl(parsed);
  return bucket !== null && OWN_STORAGE_BUCKETS.includes(bucket);
}

/**
 * The OBJECT PATH a Storage URL addresses (e.g. products/abc/description/x.png),
 * or null when the URL is not one of the recognised shapes.
 *
 * Complements storageBucketFromUrl above, which answers "whose bucket is this?".
 * This answers "which object?", which is what a caller needs in order to delete
 * it: the Storage SDK's ref() takes a path, while everything persisted on a
 * product document is a download URL.
 *
 * Deliberately does NOT check the bucket. Callers that are about to act on the
 * object must pair this with isOwnStorageUrl; callers that are merely comparing
 * two of our own URLs (which description images changed between saves?) do not
 * need to, and forcing them to would couple a string diff to a bucket list.
 */
export function storageObjectPathFromUrl(parsed: URL): string | null {
  const host = parsed.hostname.toLowerCase();

  // getDownloadURL shape: /v0/b/<bucket>/o/<percent-encoded path>
  if (host === 'firebasestorage.googleapis.com') {
    const marker = '/o/';
    const at = parsed.pathname.indexOf(marker);
    if (at === -1) return null;
    const encoded = parsed.pathname.slice(at + marker.length);
    if (!encoded) return null;
    try {
      return decodeURIComponent(encoded);
    } catch {
      return null;
    }
  }

  // Virtual-hosted style: <bucket>.storage.googleapis.com/<path>
  if (host.endsWith('.storage.googleapis.com')) {
    const path = parsed.pathname.replace(/^\//, '');
    return path || null;
  }

  // Path style: storage.googleapis.com/<bucket>/<path>
  if (host === 'storage.googleapis.com') {
    const parts = parsed.pathname.replace(/^\//, '').split('/');
    if (parts.length < 2) return null;
    return parts.slice(1).join('/') || null;
  }

  return null;
}
